import React, { useMemo } from 'react';

import GroupVsRemainderContent from 'components/GroupVsRemainder/GroupVsRemainderContent';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import CardContext from 'contexts/CardContext';

import buildGroupVsRemainderDefinition from './buildGroupVsRemainderDefinition';
import ChartPlaceholder from './ChartPlaceholder';
import LegacyGadgetBody from './LegacyGadgetBody';

// A Line in Group vs remainder mode (a block, or a Composite's element):
// drawn by the legacy Group Vs Remainder it saves as, within its dates and
// filters. The selection is the drilled-into entity or the entity page's;
// without one (as in Studio) it's the remainder's line alone. Stable while
// its settings don't change, so it doesn't refetch on a move. w and h are
// the grid size of the block it sits in.
const StudioGroupVsRemainder = ({
  content,
  name,
  w,
  h,
  isInteractive,
}: {
  content: Studio.ChartContent;
  name: string;
  w: number;
  h: number;
  isInteractive: boolean;
}) => {
  const settingsKey = JSON.stringify(content.line ?? {});
  // A new definition per settings change: the renderer writes the
  // selection onto the one it's given
  const built = useMemo(
    () =>
      buildGroupVsRemainderDefinition({
        card: {
          visType: 'line',
          description: content.description,
          line: JSON.parse(settingsKey),
        },
        id: content.id,
        name,
      }),
    // The settings are compared by value
    [content.description, content.id, name, settingsKey],
  );
  const gadget = 'chartDefinition' in built ? built.chartDefinition : undefined;
  const cardContextValue = useMemo(
    () => ({ i: content.id, w, h, chartDefinition: gadget }),
    [content.id, gadget, h, w],
  );

  if (!gadget) {
    return (
      <ChartPlaceholder
        cardId={content.id}
        visType="line"
        message={'missing' in built ? built.missing : ''}
      />
    );
  }
  return (
    <LegacyGadgetBody
      $isInteractive={isInteractive}
      data-testid={`studio-card-group-vs-remainder-${content.id}`}
    >
      <div>
        <CardContext.Provider value={cardContextValue}>
          {/* Its peer group values go to its menu (BlockMenuScope) */}
          <GqlClientProvider
            visName={gadget.name}
            visId={gadget.id}
            visType={gadget.type}
          >
            <GroupVsRemainderContent gadget={gadget} />
          </GqlClientProvider>
        </CardContext.Provider>
      </div>
    </LegacyGadgetBody>
  );
};

export default StudioGroupVsRemainder;
